import React, { useEffect, useMemo, useState } from 'react';
import {
    Portal,
    Box,
    Typography,
    IconButton,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    FormControlLabel,
    TextField,
    InputAdornment,
    Tooltip,
    Menu,
    MenuItem,
    ListSubheader,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    AccountBalance as MasavIcon,
    Search as SearchIcon,
    CreditCard as CreditCardIcon,
    NoteAdd as NoteAddIcon,
    History as HistoryIcon,
    People as PeopleIcon,
    Close as ClearIcon,
    ExpandMore as ExpandMoreIcon,
    FileDownload as ExportIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import masavService from '../../../Services/masav.service';
import ledgerReportsService from '../../../Services/ledgerReports.service';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import MasavClientsManageTab from './MasavClientsManageTab';
import MasavBatchHistoryTab from './MasavBatchHistoryTab';
import { MASAV_EXPORT_KIND, downloadMasavDraftExcel, downloadMasavDraftPdf } from './masavDraftExport';
import s from './MasavDraftModal.module.scss';

/** Exact strings returned by MasavService / MasavFileBuilder (API) → i18n keys */
const MASAV_SERVER_MESSAGE_TO_I18N = {
    'MASAV header configuration in appsettings is incomplete.': 'MasavErr_HeaderIncomplete',
    'MASAV header configuration is incomplete.': 'MasavErr_HeaderIncomplete',
    'No payable amount (יתרה נטו חיובית נדרשת להעברה).': 'MasavErr_NoPayableAmount',
    // Backend uses Unicode en-dash (\u2013) in ranges
    'Beneficiary ID: Client.ClientId must yield 5\u201315 digits after normalization (TODO: dedicated national/H.P. field).':
        'MasavErr_BeneficiaryId',
    'Bank code missing or invalid (2 digits required).': 'MasavErr_BankCode',
    'Branch number missing or invalid (3 digits required).': 'MasavErr_Branch',
    'Account number missing or invalid (4\u201312 digits required after normalization).': 'MasavErr_AccountNumber',
    'Client name is required for beneficiary name field.': 'MasavErr_ClientNameRequired',
    'Masav:InstitutionSubjectCode is required.': 'MasavErr_MasavInstitutionSubjectRequired',
    'Masav:SenderInstitutionCode is required.': 'MasavErr_MasavSenderInstitutionRequired',
    'Masav:InstitutionName is required.': 'MasavErr_MasavInstitutionNameRequired',
    'Masav:BeneficiaryAccountType must be exactly 4 digits.':
        'MasavErr_MasavBeneficiaryAccountType',
    'Masav:BeneficiaryAccountType must be exactly 2 digits (not modeled per client \u2014 configure globally).':
        'MasavErr_MasavBeneficiaryAccountType',
    'Masav:HeaderSerial should be 3 numeric digits (e.g. 001).': 'MasavErr_MasavHeaderSerial',
    'Accountant MASAV settings are incomplete; complete all fields on the accountant profile or clear them to use appsettings.':
        'MasavErr_AccountantMasavIncomplete',
    'Accountant MASAV settings are incomplete; complete all five fields on the accountant profile or clear all MASAV fields to use the server default configuration.':
        'MasavErr_AccountantMasavIncomplete',
    'Accountant MASAV settings are incomplete; complete all five fields on the accountant profile, or clear every MASAV field.':
        'MasavErr_AccountantMasavIncomplete',
    'Accountant MASAV settings are required; complete all five fields on the accountant profile.':
        'MasavErr_AccountantMasavRequired',
};

const TAB_CREATE = 0;
const TAB_HISTORY = 1;
const TAB_MANAGE = 2;

function isAccountantMasavProfileServerMessage(msg) {
    if (msg == null || typeof msg !== 'string') return false;
    return (
        msg.startsWith('Accountant MASAV settings are incomplete') ||
        msg.startsWith('Accountant MASAV settings are required')
    );
}

function translateMasavServerMessage(msg, t) {
    if (msg == null || msg === '') return '';
    const key = MASAV_SERVER_MESSAGE_TO_I18N[msg];
    return key ? t(key) : msg;
}

function pickRow(raw) {
    const errs = raw.validationErrors ?? raw.ValidationErrors ?? [];
    return {
        caseId: raw.caseId ?? raw.CaseId,
        clientName: raw.clientName ?? raw.ClientName ?? '',
        netBalance: Number(raw.netBalance ?? raw.NetBalance ?? 0),
        payableAmount: Number(raw.payableAmount ?? raw.PayableAmount ?? 0),
        bankCode: raw.bankCode ?? raw.BankCode ?? '',
        branchNumber: raw.branchNumber ?? raw.BranchNumber ?? '',
        accountNumber: raw.accountNumber ?? raw.AccountNumber ?? '',
        beneficiaryIdDigits: raw.beneficiaryIdDigits ?? raw.BeneficiaryIdDigits ?? '',
        isEligibleForDownload: raw.isEligibleForDownload ?? raw.IsEligibleForDownload ?? false,
        includeByDefault: raw.includeByDefault ?? raw.IncludeByDefault ?? false,
        validationErrors: Array.isArray(errs) ? errs : [],
        // Reconciliation breakdown (why payable differs from a single month's charge).
        monthlyChargesTotal: Number(raw.monthlyChargesTotal ?? raw.MonthlyChargesTotal ?? 0),
        currentMonthMonthlyCharge: Number(raw.currentMonthMonthlyCharge ?? raw.CurrentMonthMonthlyCharge ?? 0),
        oneTimeChargesTotal: Number(raw.oneTimeChargesTotal ?? raw.OneTimeChargesTotal ?? 0),
        paymentsTotal: Number(raw.paymentsTotal ?? raw.PaymentsTotal ?? 0),
        beyondCurrentMonth: Number(raw.beyondCurrentMonth ?? raw.BeyondCurrentMonth ?? 0),
        monthSlices: (raw.monthSlices ?? raw.MonthSlices ?? []).map(pickMonthSlice),
        chargeDescriptions: (raw.chargeDescriptions ?? raw.ChargeDescriptions ?? []).filter(Boolean),
        isPaymentArrangementApplied: !!(raw.isPaymentArrangementApplied ?? raw.IsPaymentArrangementApplied),
        paymentArrangementAmount: Number(raw.paymentArrangementAmount ?? raw.PaymentArrangementAmount ?? 0) || null,
        paymentArrangementStartYear: Number(raw.paymentArrangementStartYear ?? raw.PaymentArrangementStartYear ?? 0) || null,
        paymentArrangementStartMonth: Number(raw.paymentArrangementStartMonth ?? raw.PaymentArrangementStartMonth ?? 0) || null,
        paymentArrangementEndYear: Number(raw.paymentArrangementEndYear ?? raw.PaymentArrangementEndYear ?? 0) || null,
        paymentArrangementEndMonth: Number(raw.paymentArrangementEndMonth ?? raw.PaymentArrangementEndMonth ?? 0) || null,
        paymentDayOfMonth: raw.paymentDayOfMonth ?? raw.PaymentDayOfMonth ?? null,
        paymentDueDate: raw.paymentDueDate ?? raw.PaymentDueDate ?? null,
        isBeforePaymentDay: !!(raw.isBeforePaymentDay ?? raw.IsBeforePaymentDay),
    };
}

function pickMonthSlice(raw) {
    if (!raw) return null;
    return {
        year: Number(raw.year ?? raw.Year ?? 0),
        month: Number(raw.month ?? raw.Month ?? 0),
        monthlyCharges: Number(raw.monthlyCharges ?? raw.MonthlyCharges ?? 0),
        oneTimeCharges: Number(raw.oneTimeCharges ?? raw.OneTimeCharges ?? 0),
        chargesTotal: Number(raw.chargesTotal ?? raw.ChargesTotal ?? 0),
        paymentsInMonth: Number(raw.paymentsInMonth ?? raw.PaymentsInMonth ?? 0),
        paymentsAllocated: Number(raw.paymentsAllocated ?? raw.PaymentsAllocated ?? 0),
        unpaid: Number(raw.unpaid ?? raw.Unpaid ?? 0),
    };
}

function pickReconciliation(raw) {
    if (!raw) return null;
    return {
        currentMonthCharges: Number(raw.currentMonthCharges ?? raw.CurrentMonthCharges ?? 0),
        priorMonthlyArrears: Number(raw.priorMonthlyArrears ?? raw.PriorMonthlyArrears ?? 0),
        oneTimeCharges: Number(raw.oneTimeCharges ?? raw.OneTimeCharges ?? 0),
        payments: Number(raw.payments ?? raw.Payments ?? 0),
        payableTotal: Number(raw.payableTotal ?? raw.PayableTotal ?? 0),
        rowCount: Number(raw.rowCount ?? raw.RowCount ?? 0),
    };
}

function pickDraft(raw) {
    const hdr = raw.headerErrors ?? raw.HeaderErrors ?? [];
    return {
        rows: (raw.rows ?? raw.Rows ?? []).map(pickRow),
        headerConfigurationValid: raw.headerConfigurationValid ?? raw.HeaderConfigurationValid ?? false,
        headerErrors: Array.isArray(hdr) ? hdr : [],
        paymentDate: raw.paymentDate ?? raw.PaymentDate,
        reconciliation: pickReconciliation(raw.reconciliation ?? raw.Reconciliation ?? null),
        monthSlices: (raw.monthSlices ?? raw.MonthSlices ?? []).map(pickMonthSlice).filter(Boolean),
    };
}

const formatMonthLabel = (year, month) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(month)}/${year}`;
};

const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

/** Same value shape as CustomMonthDatePicker: UTC start of the month. */
const toUtcMonthStart = (year, monthIndex) => new Date(Date.UTC(year, monthIndex, 1));
const currentUtcMonthStart = () => {
    const now = new Date();
    return toUtcMonthStart(now.getFullYear(), now.getMonth());
};
const monthKeyOf = (d) => (d ? `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}` : '');
const DEBT_MONTHS_PREVIEW = 6;

const MasavDraftModal = ({
    direction: directionProp,
    visible,
    onClose,
    client,
    filterCaseIds,
    draftReloadNonce = 0,
    onOpenMasavProfileSettings,
    /** (rows, { batchId }) — opens the bulk receipt modal for rows of a saved batch. */
    onOpenBulkReceiptForSelection,
    /** Opens the SavedCardsModal (leading-business aware) from the CollectionPage. */
    onOpenSavedCards,
    /** Bump draft reload in parent when MASAV client bank settings change. */
    onMasavClientsChanged,
    /** Auth token for the PDF export. */
    token,
}) => {
    const { t, i18n } = useTranslation();
    /** i18n is authoritative so alerts align when language is Hebrew even if parent omits `direction` */
    const direction = i18n.dir() || directionProp || 'ltr';
    const isRtl = direction === 'rtl';
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(currentUtcMonthStart);
    const [selected, setSelected] = useState(() => new Set());
    /** When true, table lists only rows that pass MASAV validation (same as eligible for download). */
    const [showOnlyValidRows, setShowOnlyValidRows] = useState(false);
    /** When true, collect only the selected month's unpaid (FIFO) instead of the accumulated balance. */
    const [monthOnly, setMonthOnly] = useState(false);
    /** Filter table by client name / case id when several rows exist (hidden for a single row). */
    const [rowSearchQuery, setRowSearchQuery] = useState('');
    /** Per-case manual amount overrides { [caseId]: number } that replace the computed payable. */
    const [amountOverrides, setAmountOverrides] = useState({});
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [showAllDebtMonths, setShowAllDebtMonths] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_CREATE);
    /** Batch to select when the history tab opens (set right after a file is generated). */
    const [historyFocusBatchId, setHistoryFocusBatchId] = useState(null);
    const [historyReloadNonce, setHistoryReloadNonce] = useState(0);
    const [exportAnchor, setExportAnchor] = useState(null);
    const [exporting, setExporting] = useState(false);

    const accountantId = client?.accountantId;

    const normalized = useMemo(() => (draft ? pickDraft(draft) : null), [draft]);

    const displayRows = useMemo(() => {
        if (!normalized?.rows?.length) return [];
        if (!showOnlyValidRows) return normalized.rows;
        return normalized.rows.filter((r) => r.isEligibleForDownload);
    }, [normalized, showOnlyValidRows]);

    const draftRowCount = normalized?.rows?.length ?? 0;
    /** Search + “valid only” filter are only useful with more than one client row */
    const showMultiClientToolbar = draftRowCount > 1;

    const beforePaymentDayRows = useMemo(
        () => (normalized?.rows ?? []).filter((r) => r.isBeforePaymentDay),
        [normalized]
    );

    const tableRows = useMemo(() => {
        const q = rowSearchQuery.trim().toLowerCase();
        if (!q) return displayRows;
        return displayRows.filter(
            (r) =>
                String(r.clientName ?? '')
                    .toLowerCase()
                    .includes(q) || String(r.caseId ?? '').toLowerCase().includes(q)
        );
    }, [displayRows, rowSearchQuery]);

    const accountantProfileHeaderIssue = useMemo(() => {
        if (!normalized?.headerErrors?.length) return false;
        return normalized.headerErrors.some(isAccountantMasavProfileServerMessage);
    }, [normalized]);

    useEffect(() => {
        if (!visible || !draft) return;
        const n = pickDraft(draft);
        const initial = new Set();
        n.rows.forEach((r) => {
            if (r.isEligibleForDownload && r.includeByDefault) {
                initial.add(r.caseId);
            }
        });
        setSelected(initial);
    }, [visible, draft]);

    useEffect(() => {
        if (draft) setRowSearchQuery('');
    }, [draft]);

    /** Start month after end month — the draft is not reloaded until the range is valid. */
    const dateRangeInvalid = !!(startDate && endDate && startDate.getTime() > endDate.getTime());

    const loadDraft = async () => {
        const effectiveEnd = endDate;
        if (!effectiveEnd || !accountantId || dateRangeInvalid) return;
        setLoading(true);
        setError(null);
        setAmountOverrides({});
        try {
            const caseIds =
                Array.isArray(filterCaseIds) && filterCaseIds.length > 0 ? filterCaseIds : undefined;
            const data = await masavService.getMasavBulkDraft(
                accountantId,
                startDate,
                effectiveEnd,
                caseIds,
                monthOnly
            );
            setDraft(data);
        } catch (e) {
            console.error(e);
            setError(e.message || t('Error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && accountantId) {
            loadDraft();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, filterCaseIds, draftReloadNonce, monthOnly, monthKeyOf(startDate), monthKeyOf(endDate)]);

    /** Same as EditModal: the picker only commits a real date; clearing is ignored. */
    const handleEndDateChange = (date) => {
        if (date) setEndDate(date);
    };
    const handleStartDateChange = (date) => {
        if (date) setStartDate(date);
    };

    /** Click a month row → set End Date to that month (the effect reloads the draft). */
    const selectCollectMonth = (year, month) => {
        if (!monthOnly || !year || !month) return;
        setEndDate(toUtcMonthStart(year, month - 1));
    };

    const selectedCollectMonthKey = monthKeyOf(endDate);

    const toggleRow = (caseId, eligible) => {
        if (!eligible) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(caseId)) next.delete(caseId);
            else next.add(caseId);
            return next;
        });
    };

    /** Only eligible rows currently visible in the table can be selected. */
    const selectableTableRows = useMemo(
        () => tableRows.filter((r) => r.isEligibleForDownload),
        [tableRows]
    );
    const allTableRowsSelected =
        selectableTableRows.length > 0 && selectableTableRows.every((r) => selected.has(r.caseId));
    const someTableRowsSelected = selectableTableRows.some((r) => selected.has(r.caseId));

    const toggleSelectAllRows = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (allTableRowsSelected) {
                selectableTableRows.forEach((r) => next.delete(r.caseId));
            } else {
                selectableTableRows.forEach((r) => next.add(r.caseId));
            }
            return next;
        });
    };

    /** Effective payable for a row = manual override (when > 0) else the computed payable. */
    const effectivePayable = (r) => {
        const ov = amountOverrides[r.caseId];
        return ov != null && ov > 0 ? ov : r.payableAmount;
    };

    const setRowOverride = (caseId, rawValue) => {
        setAmountOverrides((prev) => {
            const next = { ...prev };
            const num = Number(rawValue);
            if (rawValue === '' || rawValue == null || Number.isNaN(num) || num <= 0) {
                delete next[caseId];
            } else {
                next[caseId] = num;
            }
            return next;
        });
    };

    const selectedRows = useMemo(() => {
        if (!normalized) return [];
        return normalized.rows.filter((r) => selected.has(r.caseId));
    }, [normalized, selected]);

    const totals = useMemo(() => {
        let sum = 0;
        let count = 0;
        selectedRows.forEach((r) => {
            const payable = effectivePayable(r);
            if (r.isEligibleForDownload && payable > 0) {
                sum += payable;
                count += 1;
            }
        });
        return { sum, count };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, amountOverrides]);

    /** How the selected rows' amount to collect is built: charges − payments ± adjustments, plus the unpaid months behind it. */
    const breakdown = useMemo(() => {
        const rows = selectedRows.filter((r) => r.isEligibleForDownload && effectivePayable(r) > 0);
        const acc = {
            count: rows.length,
            currentMonth: 0,
            priorMonthly: 0,
            oneTime: 0,
            payments: 0,
            computedPayable: 0,
            arrangementAdj: 0,
            otherAdj: 0,
            manualAdj: 0,
            toCollect: 0,
        };
        const months = new Map();
        rows.forEach((r) => {
            const balance = r.monthlyChargesTotal + r.oneTimeChargesTotal - r.paymentsTotal;
            acc.currentMonth += r.currentMonthMonthlyCharge;
            acc.priorMonthly += r.monthlyChargesTotal - r.currentMonthMonthlyCharge;
            acc.oneTime += r.oneTimeChargesTotal;
            acc.payments += r.paymentsTotal;
            if (r.isPaymentArrangementApplied) acc.arrangementAdj += r.payableAmount - balance;
            else acc.otherAdj += r.payableAmount - balance;
            acc.manualAdj += effectivePayable(r) - r.payableAmount;
            acc.toCollect += effectivePayable(r);
            (r.monthSlices || []).forEach((sl) => {
                if (!sl || sl.unpaid <= 0.005) return;
                const key = sl.year * 100 + sl.month;
                const cur = months.get(key) || { key, year: sl.year, month: sl.month, unpaid: 0, clients: 0 };
                cur.unpaid += sl.unpaid;
                cur.clients += 1;
                months.set(key, cur);
            });
        });
        acc.charges = acc.currentMonth + acc.priorMonthly + acc.oneTime;
        acc.debtMonths = [...months.values()].sort((a, b) => b.key - a.key);
        acc.debtTotal = acc.debtMonths.reduce((sum, m) => sum + m.unpaid, 0);
        acc.maxMonthUnpaid = acc.debtMonths.reduce((max, m) => Math.max(max, m.unpaid), 0);
        return acc;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRows, amountOverrides]);

    const canDownload =
        normalized?.headerConfigurationValid &&
        totals.count > 0 &&
        selectedRows.every((r) => r.isEligibleForDownload);

    /** Generate the file as a saved batch, download it, then jump to the batch in the history tab. */
    const handleDownload = async () => {
        if (!canDownload || !accountantId) return;
        setDownloading(true);
        setError(null);
        try {
            const ids = selectedRows.filter((r) => r.isEligibleForDownload).map((r) => r.caseId);
            const overridesForDownload = {};
            selectedRows.forEach((r) => {
                if (r.isEligibleForDownload && amountOverrides[r.caseId] > 0) {
                    overridesForDownload[r.caseId] = amountOverrides[r.caseId];
                }
            });
            const batch = await masavService.generateMasavBatch(
                accountantId,
                startDate,
                endDate,
                ids,
                overridesForDownload,
                monthOnly
            );
            const { blob, fileName } = await masavService.downloadMasavBatch(accountantId, batch);
            const didDownload = await ledgerReportsService.DownloadFile(blob, fileName);
            if (didDownload) {
                toast.success(t('MasavBatchGenerated'));
            } else {
                toast.info(t('MasavBatchSavedDownloadCancelled'));
            }
            setHistoryFocusBatchId(batch.id ?? batch.Id);
            setHistoryReloadNonce((n) => n + 1);
            setActiveTab(TAB_HISTORY);
        } catch (e) {
            console.error(e);
            setError(translateMasavServerMessage(e.message, t) || t('Error'));
        } finally {
            setDownloading(false);
        }
    };

    const canExport = !!accountantId && !!endDate && !dateRangeInvalid && !loading && !exporting;

    const toMasavExportRow = (r) => ({
        ...r,
        payable: effectivePayable(r),
        status: [
            r.isEligibleForDownload ? t('Valid') : null,
            r.isBeforePaymentDay ? t('MasavPaymentDayNotYetChip', { date: formatDate(r.paymentDueDate) }) : null,
            ...r.validationErrors.map((err) => translateMasavServerMessage(err, t)),
        ]
            .filter(Boolean)
            .join(' · '),
    });

    /** MASAV clients: rows currently shown in the table. Non-MASAV clients: fetched with the same period / mode. */
    const handleExport = async (kind, format) => {
        setExportAnchor(null);
        if (!canExport) return;
        setExporting(true);
        try {
            let rows;
            if (kind === MASAV_EXPORT_KIND.MASAV) {
                rows = tableRows.map(toMasavExportRow);
            } else {
                const caseIds =
                    Array.isArray(filterCaseIds) && filterCaseIds.length > 0 ? filterCaseIds : undefined;
                const data = await masavService.getMasavBulkDraft(
                    accountantId,
                    startDate,
                    endDate,
                    caseIds,
                    monthOnly,
                    true
                );
                rows = pickDraft(data)
                    .rows.map((r) => ({ ...r, payable: r.payableAmount }))
                    .sort((a, b) => String(a.clientName).localeCompare(String(b.clientName), i18n.language));
            }
            if (rows.length === 0) {
                toast.info(t('MasavExportNoRows'));
                return;
            }
            const options = { kind, rows, client, startDate, endDate, monthOnly, direction, t };
            if (format === 'pdf') {
                await downloadMasavDraftPdf({ ...options, token });
            } else {
                downloadMasavDraftExcel(options);
            }
        } catch (e) {
            console.error(e);
            toast.error(t('MasavExportFailed'));
        } finally {
            setExporting(false);
        }
    };

    const renderExportMenu = () => {
        const groups = [
            { kind: MASAV_EXPORT_KIND.MASAV, label: t('MasavExportMasavClients'), disabled: tableRows.length === 0 },
            { kind: MASAV_EXPORT_KIND.NON_MASAV, label: t('MasavExportNonMasavClients'), disabled: false },
        ];
        return (
            <Menu
                anchorEl={exportAnchor}
                open={!!exportAnchor}
                onClose={() => setExportAnchor(null)}
                sx={{ zIndex: 1400 }}
                MenuListProps={{ dense: true, dir: direction }}
            >
                {groups.flatMap((g) => [
                    <ListSubheader key={`${g.kind}-h`} sx={{ lineHeight: '32px', fontWeight: 700 }}>
                        {g.label}
                    </ListSubheader>,
                    <MenuItem key={`${g.kind}-pdf`} disabled={g.disabled} onClick={() => handleExport(g.kind, 'pdf')}>
                        <ListItemIcon>
                            <PdfIcon fontSize="small" sx={{ color: '#D32F2F' }} />
                        </ListItemIcon>
                        <ListItemText>{t('DownloadPDFFile')}</ListItemText>
                    </MenuItem>,
                    <MenuItem key={`${g.kind}-xlsx`} disabled={g.disabled} onClick={() => handleExport(g.kind, 'excel')}>
                        <ListItemIcon>
                            <ExcelIcon fontSize="small" sx={{ color: '#1E7145' }} />
                        </ListItemIcon>
                        <ListItemText>{t('DownloadExcelFile')}</ListItemText>
                    </MenuItem>,
                ])}
            </Menu>
        );
    };

    const handleClose = () => {
        setDraft(null);
        setError(null);
        setStartDate(null);
        setEndDate(currentUtcMonthStart());
        setShowOnlyValidRows(false);
        setMonthOnly(false);
        setRowSearchQuery('');
        setAmountOverrides({});
        setActiveTab(TAB_CREATE);
        setHistoryFocusBatchId(null);
        onClose();
    };

    /**
     * Do not use flexDirection: row-reverse when dir="rtl" — the Alert already has dir set,
     * so a normal row places the icon on the inline-start side (right in RTL).
     */
    const masavAlertSx = {
        mb: 2,
        borderRadius: '10px',
        alignItems: 'flex-start',
        '& .MuiAlert-icon': {
            marginInlineEnd: 2,
            py: 0.875,
        },
        '& .MuiAlert-message': {
            width: '100%',
            flex: '1 1 auto',
            minWidth: 0,
            py: 1,
        },
    };

    if (!visible) return null;

    const tabs = [
        { id: TAB_CREATE, label: t('MasavTabCreateFile'), icon: <NoteAddIcon fontSize="small" /> },
        { id: TAB_HISTORY, label: t('MasavTabHistory'), icon: <HistoryIcon fontSize="small" /> },
        { id: TAB_MANAGE, label: t('MasavTabManageClients'), icon: <PeopleIcon fontSize="small" /> },
    ];

    const eligibleCount = (normalized?.rows ?? []).filter((r) => r.isEligibleForDownload).length;

    const renderPaymentDayChip = (r) => (
        <span className={`${s.chip} ${s.chipWarning} ${s.payDayChip}`}>
            {t('MasavPaymentDayNotYetChip', { date: formatDate(r.paymentDueDate) })}
        </span>
    );

    const renderValidation = (r) => (
        <div className={s.chipRow}>
            {r.isEligibleForDownload && <span className={`${s.chip} ${s.chipSuccess}`}>{t('Valid')}</span>}
            {r.isBeforePaymentDay && renderPaymentDayChip(r)}
            {r.validationErrors.map((err, i) => (
                <span key={i} className={`${s.chip} ${s.chipError} ${s.payDayChip}`}>
                    {translateMasavServerMessage(err, t)}
                </span>
            ))}
            {!r.isBeforePaymentDay && r.paymentDayOfMonth && r.paymentDueDate && (
                <span className={`${s.chip} ${s.chipMuted}`}>
                    {t('MasavPaymentDayChip', { day: r.paymentDayOfMonth })}
                </span>
            )}
        </div>
    );

    const renderBreakdownLine = ({ key, op, label, value, kind, negative }) => (
        <div key={key} className={`${s.eqLine}${kind ? ` ${s[kind]}` : ''}`}>
            <span className={s.eqOp}>{op}</span>
            <span className={s.eqLabel}>{label}</span>
            <span className={`${s.eqValue}${negative ? ` ${s.negative}` : ''}`}>{beautifyNumber(value)}</span>
        </div>
    );

    const renderBreakdown = () => {
        const b = breakdown;
        const hasRows = b.count > 0;
        const adjLines = [
            { key: 'arr', label: t('MasavReconArrangementAdj'), value: b.arrangementAdj },
            { key: 'other', label: t('MasavReconOtherAdj'), value: b.otherAdj },
            { key: 'manual', label: t('MasavReconManualAdj'), value: b.manualAdj },
        ].filter((l) => Math.abs(l.value) > 0.005);
        const endYear = endDate?.getUTCFullYear();
        const endMonth = endDate ? endDate.getUTCMonth() + 1 : null;
        const visibleMonths = showAllDebtMonths ? b.debtMonths : b.debtMonths.slice(0, DEBT_MONTHS_PREVIEW);
        const hiddenMonths = b.debtMonths.slice(visibleMonths.length);
        const hiddenTotal = hiddenMonths.reduce((sum, m) => sum + m.unpaid, 0);
        const showAging = !monthOnly;

        return (
            <div className={s.panel}>
                <button
                    type="button"
                    className={s.breakdownToggle}
                    onClick={() => setBreakdownOpen((v) => !v)}
                    aria-expanded={breakdownOpen}
                >
                    <span className={s.sectionTitle}>{t('MasavReconToggle')}</span>
                    <span className={s.chip}>{t('MasavReconSelectedCount', { count: b.count })}</span>
                    {hasRows && (
                        <span className={s.breakdownSummary}>
                            <span>{t('MasavReconChargesTotal')}</span>
                            <b className={s.eqValue}>{beautifyNumber(b.charges)}</b>
                            <span className={s.eqOp}>−</span>
                            <span>{t('MasavReconPayments')}</span>
                            <b className={s.eqValue}>{beautifyNumber(b.payments)}</b>
                            {adjLines.length > 0 && <span className={s.eqOp}>±</span>}
                            {adjLines.length > 0 && <span>{t('MasavReconAdjustments')}</span>}
                            <span className={s.eqOp}>=</span>
                            <b className={`${s.eqValue} ${s.eqTotalValue}`}>{beautifyNumber(b.toCollect)}</b>
                        </span>
                    )}
                    <ExpandMoreIcon
                        className={s.breakdownChevron}
                        style={{ transform: breakdownOpen ? 'rotate(180deg)' : undefined }}
                    />
                </button>

                {breakdownOpen && !hasRows && (
                    <p className={s.muted} style={{ marginTop: 10 }}>{t('MasavReconNoSelection')}</p>
                )}

                {breakdownOpen && hasRows && (
                    <div className={`${s.breakdownGrid}${showAging ? '' : ` ${s.breakdownGridSingle}`}`}>
                        <div className={s.breakdownCol}>
                            {renderBreakdownLine({ key: 'cur', op: '', label: t('MasavReconCurrentMonth'), value: b.currentMonth })}
                            {Math.abs(b.priorMonthly) > 0.005 &&
                                renderBreakdownLine({ key: 'prior', op: '+', label: t('MasavReconArrears'), value: b.priorMonthly })}
                            {Math.abs(b.oneTime) > 0.005 &&
                                renderBreakdownLine({ key: 'one', op: '+', label: t('MasavReconOneTime'), value: b.oneTime })}
                            {renderBreakdownLine({ key: 'charges', op: '=', label: t('MasavReconChargesTotal'), value: b.charges, kind: 'eqSubtotal' })}
                            {renderBreakdownLine({ key: 'pay', op: '−', label: t('MasavReconPayments'), value: b.payments, negative: true })}
                            {adjLines.map((l) =>
                                renderBreakdownLine({
                                    key: l.key,
                                    op: l.value < 0 ? '−' : '+',
                                    label: l.label,
                                    value: Math.abs(l.value),
                                    negative: l.value < 0,
                                })
                            )}
                            {renderBreakdownLine({ key: 'total', op: '=', label: t('MasavReconPayable'), value: b.toCollect, kind: 'eqTotal' })}
                        </div>

                        {showAging && (
                            <div className={s.breakdownCol}>
                                <div className={s.agingHeader}>
                                    <span className={s.eqLabel} style={{ fontWeight: 700 }}>{t('MasavAgingTitle')}</span>
                                    <span className={s.muted}>{t('MasavAgingHint')}</span>
                                </div>
                                {b.debtMonths.length === 0 ? (
                                    <p className={s.muted}>{t('MasavAgingEmpty')}</p>
                                ) : (
                                    <>
                                        {visibleMonths.map((m) => {
                                            const isEnd = m.year === endYear && m.month === endMonth;
                                            const pct = b.maxMonthUnpaid > 0 ? (m.unpaid / b.maxMonthUnpaid) * 100 : 0;
                                            return (
                                                <div key={m.key} className={s.agingRow}>
                                                    <span className={s.agingMonth}>
                                                        {formatMonthLabel(m.year, m.month)}
                                                        {isEnd && <span className={s.chip}>{t('MasavSelectedMonth')}</span>}
                                                        {b.count > 1 && (
                                                            <span className={s.muted}>{t('MasavAgingClients', { count: m.clients })}</span>
                                                        )}
                                                    </span>
                                                    <span className={s.agingBar}>
                                                        <span
                                                            className={`${s.agingBarFill}${isEnd ? '' : ` ${s.agingBarFillOld}`}`}
                                                            style={{ width: `${Math.max(pct, 2)}%` }}
                                                        />
                                                    </span>
                                                    <span className={s.eqValue}>{beautifyNumber(m.unpaid)}</span>
                                                </div>
                                            );
                                        })}
                                        {hiddenMonths.length > 0 && (
                                            <button
                                                type="button"
                                                className={s.agingMore}
                                                onClick={() => setShowAllDebtMonths(true)}
                                            >
                                                <span>{t('MasavAgingOlder', { count: hiddenMonths.length })}</span>
                                                <span className={s.eqValue}>{beautifyNumber(hiddenTotal)}</span>
                                            </button>
                                        )}
                                        {renderBreakdownLine({ key: 'debt', op: '=', label: t('MasavAgingTotal'), value: b.debtTotal, kind: 'eqSubtotal' })}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderCreateTab = () => (
        <>
            {error && (
                <Alert dir={direction} severity="error" sx={masavAlertSx} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {normalized && !normalized.headerConfigurationValid && (
                <Alert dir={direction} severity="warning" sx={masavAlertSx}>
                    <Typography component="div" variant="body2">
                        {accountantProfileHeaderIssue
                            ? t('MasavHeaderAccountantConfigInvalid')
                            : t('MasavHeaderConfigInvalid')}
                    </Typography>
                    {typeof onOpenMasavProfileSettings === 'function' && accountantProfileHeaderIssue && (
                        <Box sx={{ mt: 1.5 }}>
                            <button type="button" className={`${s.secondaryBtn} ${s.smallBtn}`} onClick={() => onOpenMasavProfileSettings()}>
                                {t('MasavOpenProfileSettings')}
                            </button>
                        </Box>
                    )}
                    {normalized.headerErrors?.length > 0 && (
                        <Box component="ul" sx={{ mt: 1, mb: 0, paddingInlineStart: 2.5 }}>
                            {normalized.headerErrors.map((h, i) => (
                                <li key={i} dir={direction}>
                                    <Typography variant="body2" component="span">
                                        {translateMasavServerMessage(h, t)}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            <div className={s.toolbar}>
                <div className={s.toolbarGroup}>
                <div className={s.dateField}>
                        <span className={s.fieldLabel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {t('StartDate')} ({t('Optional')})
                            {startDate && (
                                <Tooltip title={t('Clear')}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setStartDate(null)}
                                        sx={{ p: 0, ml: 'auto' }}
                                        aria-label={t('Clear')}
                                    >
                                        <ClearIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </span>
                        <CustomMonthDatePicker
                            date={startDate}
                            onDateChange={handleStartDateChange}
                            height="40px"
                            disabled={loading}
                        />
                    </div>
                    <div className={s.dateField}>
                        <span className={s.fieldLabel}>
                            {t('EndDate')} ({t('Required')})
                        </span>
                        <CustomMonthDatePicker
                            date={endDate}
                            onDateChange={handleEndDateChange}
                            height="40px"
                            disabled={loading}
                        />
                    </div>
                   
                    {dateRangeInvalid && <span className={s.dateError}>{t('MasavDateRangeInvalid')}</span>}
                </div>
                <div className={s.toolbarGroup}>
                    <FormControlLabel
                        sx={{ m: 0, userSelect: 'none' }}
                        control={
                            <Checkbox
                                size="small"
                                checked={monthOnly}
                                onChange={(e) => setMonthOnly(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label={
                            <Tooltip title={t('MasavMonthOnlyHint')}>
                                <span className={s.muted}>{t('MasavMonthOnly')}</span>
                            </Tooltip>
                        }
                    />
                    <button
                        type="button"
                        className={s.secondaryBtn}
                        onClick={() => loadDraft()}
                        disabled={loading || !endDate || dateRangeInvalid}
                    >
                        {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                        {t('MasavRefreshDraft')}
                    </button>
                    <button
                        type="button"
                        className={s.secondaryBtn}
                        onClick={(e) => setExportAnchor(e.currentTarget)}
                        disabled={!canExport}
                        aria-haspopup="menu"
                    >
                        {exporting ? <CircularProgress size={16} /> : <ExportIcon fontSize="small" />}
                        {t('Export')}
                    </button>
                    {renderExportMenu()}
                </div>
            </div>

            {!loading && normalized && draftRowCount > 0 && (
                <div className={s.stats}>
                    <div className={s.statCard}>
                        <span className={s.statLabel}>{t('MasavStatClients')}</span>
                        <span className={s.statValue}>{draftRowCount}</span>
                    </div>
                    <div className={s.statCard}>
                        <span className={s.statLabel}>{t('MasavStatValid')}</span>
                        <span className={s.statValue}>{eligibleCount}</span>
                    </div>
                    <div className={s.statCard}>
                        <span className={s.statLabel}>{t('MasavStatSelected')}</span>
                        <span className={s.statValue}>{totals.count}</span>
                    </div>
                    <div className={s.statCard}>
                        <span className={s.statLabel}>{t('MasavStatTotal')}</span>
                        <span className={s.statValue}>{beautifyNumber(totals.sum)} ₪</span>
                    </div>
                    {beforePaymentDayRows.length > 0 && (
                        <Tooltip title={t('MasavBeforePaymentDaySummary', { count: beforePaymentDayRows.length })}>
                            <div className={`${s.statCard} ${s.statCardAccent}`}>
                                <span className={s.statLabel}>{t('MasavStatBeforePayDay')}</span>
                                <span className={s.statValue}>{beforePaymentDayRows.length}</span>
                            </div>
                        </Tooltip>
                    )}
                </div>
            )}

            {loading && (
                <div className={s.loading}>
                    <CircularProgress className={s.spinner} />
                </div>
            )}

            {!loading && normalized && draftRowCount === 0 && !(monthOnly && (normalized.monthSlices || []).length > 0) && (
                <div className={s.emptyState}>
                    <p className={s.muted} style={{ marginBottom: 12 }}>{t('MasavNoClientsConfigured')}</p>
                    <button type="button" className={s.secondaryBtn} onClick={() => setActiveTab(TAB_MANAGE)}>
                        <PeopleIcon fontSize="small" />
                        {t('MasavTabManageClients')}
                    </button>
                </div>
            )}

            {!loading && normalized && draftRowCount === 0 && monthOnly && (normalized.monthSlices || []).length > 0 && (
                <div className={s.emptyState}>{t('MasavNoUnpaidInSelectedMonth')}</div>
            )}

            {!loading && normalized && draftRowCount > 0 && (
                <>
                    {showMultiClientToolbar && (
                        <Box
                            sx={{
                                mb: 1.5,
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                            }}
                        >
                            <TextField
                                size="small"
                                value={rowSearchQuery}
                                onChange={(e) => setRowSearchQuery(e.target.value)}
                                placeholder={t('MasavDraftSearchRowsPlaceholder')}
                                aria-label={t('MasavDraftSearchRowsPlaceholder')}
                                inputProps={{ dir: direction }}
                                sx={{ flex: '1 1 220px', maxWidth: 340, '& .MuiInputBase-root': { borderRadius: '10px', bgcolor: '#fff' } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action" aria-hidden />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={showOnlyValidRows}
                                        onChange={(e) => setShowOnlyValidRows(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={<span className={s.muted}>{t('MasavShowOnlyValidRows')}</span>}
                                sx={{ m: 0, userSelect: 'none' }}
                            />
                        </Box>
                    )}
                    <div className={s.tableWrap} dir={direction} style={{ maxHeight: 460 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" sx={{ width: 48 }}>
                                        <Checkbox
                                            size="small"
                                            checked={allTableRowsSelected}
                                            indeterminate={someTableRowsSelected && !allTableRowsSelected}
                                            onChange={toggleSelectAllRows}
                                            disabled={selectableTableRows.length === 0}
                                        />
                                    </TableCell>
                                    <TableCell>{t('ClientName')}</TableCell>
                                    <TableCell>{t('Case')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavNetBalance')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavPayableAmount')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavBeyondMonth')}</TableCell>
                                    <TableCell>{t('Bank')}</TableCell>
                                    <TableCell>{t('Branch')}</TableCell>
                                    <TableCell>{t('AccountNumber')}</TableCell>
                                    <TableCell>{t('ClientId')}</TableCell>
                                    <TableCell sx={{ minWidth: 200 }}>{t('MasavValidation')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tableRows.map((r) => (
                                    <TableRow
                                        key={r.caseId}
                                        sx={r.isBeforePaymentDay ? { bgcolor: 'rgba(249, 170, 42, 0.07) !important' } : undefined}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selected.has(r.caseId)}
                                                disabled={!r.isEligibleForDownload}
                                                onChange={() => toggleRow(r.caseId, r.isEligibleForDownload)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <span className={s.clientName}>{r.clientName}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={s.clientCase}>{r.caseId}</span>
                                        </TableCell>
                                        <TableCell className={s.num}>
                                            <span className={Number(r.netBalance) < 0 ? s.negative : undefined}>
                                                {beautifyNumber(r.netBalance)}
                                            </span>
                                        </TableCell>
                                        <TableCell className={s.num}>
                                            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={amountOverrides[r.caseId] ?? ''}
                                                    placeholder={r.payableAmount.toFixed(2)}
                                                    onChange={(e) => setRowOverride(r.caseId, e.target.value)}
                                                    disabled={!r.isEligibleForDownload}
                                                    inputProps={{ min: 0, step: '0.01' }}
                                                    className={s.amountField}
                                                />
                                                {r.isPaymentArrangementApplied && (
                                                    <Tooltip
                                                        title={t('PaymentArrangementAppliedHint', {
                                                            amount: Number(r.paymentArrangementAmount || r.payableAmount).toFixed(2),
                                                            from: formatMonthLabel(r.paymentArrangementStartYear, r.paymentArrangementStartMonth),
                                                            to: formatMonthLabel(r.paymentArrangementEndYear, r.paymentArrangementEndMonth),
                                                        })}
                                                    >
                                                        <span className={s.chip}>
                                                            {t('PaymentArrangementBadge', {
                                                                amount: Number(r.paymentArrangementAmount || r.payableAmount).toFixed(2),
                                                            })}
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell className={s.num}>
                                            <Tooltip
                                                title={`${t('MasavReconCurrentMonth')}: ${beautifyNumber(r.currentMonthMonthlyCharge)} · ${t('MasavReconOneTime')}: ${beautifyNumber(r.oneTimeChargesTotal)} · ${t('MasavReconPayments')}: ${beautifyNumber(r.paymentsTotal)}`}
                                            >
                                                <span
                                                    style={{
                                                        color: Math.abs(r.beyondCurrentMonth) > 0.005 ? '#B26A00' : '#90A4AE',
                                                        fontWeight: Math.abs(r.beyondCurrentMonth) > 0.005 ? 700 : 400,
                                                    }}
                                                >
                                                    {beautifyNumber(r.beyondCurrentMonth)}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{r.bankCode}</TableCell>
                                        <TableCell>{r.branchNumber}</TableCell>
                                        <TableCell>{r.accountNumber}</TableCell>
                                        <TableCell>{r.beneficiaryIdDigits}</TableCell>
                                        <TableCell sx={{ maxWidth: 300 }}>{renderValidation(r)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    {draftRowCount > 0 && renderBreakdown()}
                </>
            )}

            {/* Per-month charges / payments / unpaid (FIFO). Click a month to collect it. */}
            {!loading && normalized && monthOnly && (
                <div className={s.panel}>
                    <h4 className={s.sectionTitle}>{t('MasavMonthSlicesTitle')}</h4>
                    <p className={s.muted} style={{ marginBottom: 10 }}>{t('MasavMonthSlicesHint')}</p>
                    <div className={s.tableWrap} dir={direction} style={{ maxHeight: 360 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Month')}</TableCell>
                                    <TableCell className={s.num}>{t('MonthlyCharges')}</TableCell>
                                    <TableCell className={s.num}>{t('OneTimeCharges')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavMonthPaymentsInMonth')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavMonthPaymentsAllocated')}</TableCell>
                                    <TableCell className={s.num}>{t('MasavMonthUnpaid')}</TableCell>
                                    <TableCell>{t('MasavCollectMonth')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(normalized.monthSlices || []).length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ textAlign: 'center !important', color: 'text.secondary', py: 2 }}>
                                            {t('NoData') || '—'}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    (normalized.monthSlices || []).map((slice) => {
                                        const key = `${slice.year}-${slice.month}`;
                                        const isSelected = key === selectedCollectMonthKey;
                                        return (
                                            <TableRow
                                                key={key}
                                                selected={isSelected}
                                                onClick={() => selectCollectMonth(slice.year, slice.month)}
                                                sx={{ cursor: 'pointer', ...(isSelected ? { bgcolor: 'rgba(48, 79, 255, 0.08) !important' } : {}) }}
                                            >
                                                <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>
                                                    {formatMonthLabel(slice.year, slice.month)}
                                                    {isSelected && (
                                                        <span className={s.chip} style={{ marginInlineStart: 8 }}>
                                                            {t('MasavSelectedMonth')}
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell className={s.num}>{beautifyNumber(slice.monthlyCharges)}</TableCell>
                                                <TableCell className={s.num}>{beautifyNumber(slice.oneTimeCharges)}</TableCell>
                                                <TableCell className={s.num}>{beautifyNumber(slice.paymentsInMonth)}</TableCell>
                                                <TableCell className={s.num}>{beautifyNumber(slice.paymentsAllocated)}</TableCell>
                                                <TableCell className={s.num}>
                                                    <span className={slice.unpaid > 0.005 ? s.negative : s.muted}>
                                                        {beautifyNumber(slice.unpaid)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <button
                                                        type="button"
                                                        className={`${isSelected ? s.primaryBtn : s.secondaryBtn} ${s.smallBtn}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            selectCollectMonth(slice.year, slice.month);
                                                        }}
                                                    >
                                                        {isSelected ? t('MasavCollectingThisMonth') : t('MasavCollectThisMonth')}
                                                    </button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </>
    );

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={handleClose}>
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Close')}>
                                    <IconButton onClick={handleClose} className={s.closeIcon} aria-label={t('Close')}>
                                        <CancelIcon className={s.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div className={s.headerTitle}>
                                    <MasavIcon />
                                    {t('MasavDraftTitle')}
                                </div>
                                <div />
                            </div>

                            <div className={s.tabs}>
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        className={`${s.tab}${activeTab === tab.id ? ` ${s.tabActive}` : ''}`}
                                        onClick={() => setActiveTab(tab.id)}
                                    >
                                        {tab.icon}
                                        {tab.label}
                                        {tab.id === TAB_CREATE && beforePaymentDayRows.length > 0 && (
                                            <Tooltip title={t('MasavBeforePaymentDaySummary', { count: beforePaymentDayRows.length })}>
                                                <span className={s.tabBadge}>{beforePaymentDayRows.length}</span>
                                            </Tooltip>
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className={s.scroll}>
                                <div style={{ display: activeTab === TAB_CREATE ? 'block' : 'none' }}>
                                    {renderCreateTab()}
                                </div>
                                <div style={{ display: activeTab === TAB_HISTORY ? 'block' : 'none' }}>
                                    <MasavBatchHistoryTab
                                        active={activeTab === TAB_HISTORY}
                                        accountantId={accountantId}
                                        direction={direction}
                                        focusBatchId={historyFocusBatchId}
                                        reloadNonce={historyReloadNonce + draftReloadNonce}
                                        onOpenBulkReceiptForSelection={onOpenBulkReceiptForSelection}
                                    />
                                </div>
                                <div style={{ display: activeTab === TAB_MANAGE ? 'block' : 'none' }}>
                                    <MasavClientsManageTab
                                        active={activeTab === TAB_MANAGE}
                                        accountantId={accountantId}
                                        direction={direction}
                                        onMasavClientsChanged={onMasavClientsChanged}
                                    />
                                </div>
                            </div>

                            {activeTab === TAB_CREATE && (
                                <div className={s.footer}>
                                    {typeof onOpenSavedCards === 'function' && (
                                        <button type="button" className={s.ghostBtn} onClick={() => onOpenSavedCards()}>
                                            <CreditCardIcon fontSize="small" />
                                            {t('SavedCards.manage')}
                                        </button>
                                    )}
                                    <span className={s.footerSpacer} />
                                    {totals.count > 0 ? (
                                        <span className={s.footerTotals}>
                                            {t('MasavFooterSelected', { count: totals.count, sum: beautifyNumber(totals.sum) })}
                                        </span>
                                    ) : (
                                        <span className={s.muted}>{t('MasavSelectToDownload')}</span>
                                    )}
                                    <button
                                        type="button"
                                        className={s.primaryBtn}
                                        disabled={!canDownload || downloading}
                                        onClick={handleDownload}
                                    >
                                        {downloading ? (
                                            <CircularProgress color="inherit" size={16} />
                                        ) : (
                                            <DownloadIcon fontSize="small" />
                                        )}
                                        {t('MasavDownloadFile')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MasavDraftModal;
